import { useTranslation } from "@sagot/i18n";
import { Badge, Button, cn, EmptyState, ErrorState, Icon, Input, Skeleton } from "@sagot/ui";
import { useEffect, useState } from "react";
import { FilterMenu } from "./filter-menu";
import { activeFilterCount, INBOX_VIEWS, type ConversationItem } from "./inbox-model";
import { useInboxStore } from "./inbox-store";
import { ConversationListItem } from "./conversation-list-item";
import { useNow } from "./use-now";
import type { QueueInfo } from "./queue-sort";
import type { StreamState } from "./use-inbox-stream";

const SEARCH_DEBOUNCE_MS = 300;
/** Waiting times count seconds, so the clock of a list that shows them ticks every second. */
const QUEUE_CLOCK_MS = 1_000;

export interface ListData {
  items: readonly ConversationItem[];
  /** The weighted queue's order and waiting times for the Needs staff view (live chat ops only). */
  queue?: QueueInfo | null;
  counts: Readonly<Record<string, number>>;
  isPending: boolean;
  isError: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  retry: () => void;
  loadMore: () => void;
}

function ListSkeleton() {
  return (
    <ul className="flex-1 divide-y divide-line overflow-hidden" aria-hidden="true">
      {Array.from({ length: 7 }, (_, i) => (
        <li key={i} className="flex gap-3 px-4 py-3">
          <Skeleton className="size-9 rounded-pill" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-[60%]" />
            <Skeleton className="h-3 w-[85%]" />
            <Skeleton className="h-5 w-[50%]" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function ViewChips({ counts }: { counts: Readonly<Record<string, number>> }) {
  const { t } = useTranslation();
  const view = useInboxStore((s) => s.filters.view);
  const setView = useInboxStore((s) => s.setView);
  return (
    <div role="group" aria-label={t("engage.inbox.views.label")} className="flex flex-wrap gap-1.5">
      {INBOX_VIEWS.map((v) => {
        const count = v === "needs_staff" || v === "snoozed" ? counts[v] : undefined;
        return (
          <button
            key={v}
            type="button"
            aria-pressed={view === v}
            onClick={() => { setView(v); }}
            className="rounded-pill border border-line px-2.5 py-1 text-xs font-semibold text-fg-muted hover:text-fg aria-pressed:border-primary aria-pressed:bg-primary-soft aria-pressed:text-primary-ink"
          >
            {t(`engage.inbox.views.${v}`)}
            {count ? (
              <>
                {" · "}
                <span className={v === "needs_staff" ? "text-danger" : undefined} data-testid={`view-count-${v}`}>{count}</span>
              </>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function SearchBox() {
  const { t } = useTranslation();
  const q = useInboxStore((s) => s.filters.q);
  const setQuery = useInboxStore((s) => s.setQuery);
  const [value, setValue] = useState(q);
  useEffect(() => {
    const timer = setTimeout(() => { if (value !== q) setQuery(value); }, SEARCH_DEBOUNCE_MS);
    return () => { clearTimeout(timer); };
  }, [value, q, setQuery]);
  return (
    <div className="relative">
      <Icon name="search" className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
      <Input type="search" className="h-9 pl-9" placeholder={t("engage.inbox.search")} aria-label={t("engage.inbox.search")} value={value} onChange={(e) => { setValue(e.target.value); }} />
    </div>
  );
}

function LiveBadge({ stream }: { stream: StreamState }) {
  const { t } = useTranslation();
  const live = stream === "live";
  return (
    <Badge tone={live ? "success" : "warning"} dot title={t("engage.inbox.liveHint")} data-testid="inbox-live">
      {t(`engage.inbox.${stream}`)}
    </Badge>
  );
}

/** Nothing to list: the Snoozed view explains what lands there; any other view offers to clear the search and filters. */
function ListEmpty({ snoozed, onClear }: { snoozed: boolean; onClear: () => void }) {
  const { t } = useTranslation();
  if (snoozed) {
    return (
      <div className="flex flex-1 flex-col" data-testid="snoozed-empty">
        <EmptyState className="flex-1" icon="clock" title={t("engage.inbox.snooze.emptyTitle")} description={t("engage.inbox.snooze.emptyBody")} />
      </div>
    );
  }
  return (
    <EmptyState
      className="flex-1"
      icon="inbox"
      title={t("engage.inbox.list.emptyTitle")}
      description={t("engage.inbox.list.emptyBody")}
      action={<Button variant="secondary" size="sm" onClick={onClear}>{t("engage.inbox.list.clearSearch")}</Button>}
    />
  );
}

/** Left pane: title, live status, filters, views, search, rows, load older. */
export function ConversationList({ data, selectedId, stream, className }: { data: ListData; selectedId: string | null; stream: StreamState; className?: string }) {
  const { t } = useTranslation();
  const view = useInboxStore((s) => s.filters.view);
  const narrowed = useInboxStore((s) => s.filters.q.trim() !== "" || activeFilterCount(s.filters) > 0);
  const clearAll = useInboxStore((s) => s.clearAll);
  const searchKey = useInboxStore((s) => s.searchKey);
  const now = useNow(data.queue ? QUEUE_CLOCK_MS : undefined);
  const tags = [...new Set(data.items.flatMap((i) => i.tags))].sort();
  const total = data.counts[view];
  return (
    <section aria-labelledby="inbox-title" className={cn("flex min-h-0 w-full shrink-0 flex-col border-r border-line bg-surface lg:w-80", className)}>
      <div className="space-y-3 border-b border-line px-4 pb-3 pt-4">
        <div className="flex items-center justify-between">
          <h1 id="inbox-title" className="text-lg font-extrabold tracking-tight">{t("engage.inbox.title")}</h1>
          <div className="flex items-center gap-1">
            <LiveBadge stream={stream} />
            <FilterMenu tagOptions={tags} />
          </div>
        </div>
        <ViewChips counts={data.counts} />
        <SearchBox key={searchKey} />
      </div>
      {data.isPending ? (
        <ListSkeleton />
      ) : data.isError ? (
        <ErrorState className="flex-1" headingLevel={2} title={t("engage.inbox.list.loadError")} onRetry={data.retry} retryLabel={t("common.retry")} />
      ) : data.items.length === 0 ? (
        <ListEmpty snoozed={view === "snoozed" && !narrowed} onClear={clearAll} />
      ) : (
        <>
          {data.queue ? <p className="border-b border-line px-4 py-1.5 text-[11px] text-fg-subtle" data-testid="queue-sorted-by">{t("routingLive.queue.inbox.sortedBy")}</p> : null}
          <ul aria-label={t("engage.inbox.list.label")} className="flex-1 divide-y divide-line overflow-y-auto" data-testid="conversation-list">
            {data.items.map((item) => (
              <ConversationListItem key={item.id} item={item} selected={item.id === selectedId} now={now} queue={data.queue?.entries.get(item.id)} queueFetchedAt={data.queue?.fetchedAt} />
            ))}
          </ul>
        </>
      )}
      <div className="flex items-center justify-between border-t border-line px-4 py-2.5 text-[11px] text-fg-subtle">
        <span>{total === undefined ? t("engage.inbox.list.shownOpen", { shown: data.items.length }) : t("engage.inbox.list.shown", { shown: data.items.length, total })}</span>
        {data.hasMore ? (
          <button type="button" className="font-semibold text-primary-ink hover:underline disabled:opacity-60" disabled={data.loadingMore} onClick={data.loadMore}>
            {t("engage.inbox.list.loadOlder")}
          </button>
        ) : null}
      </div>
    </section>
  );
}
